import type { DefaultTheme } from 'styled-components/macro';
import styled from 'styled-components/macro';

import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';

type ReportStateStyledT = {
  isReportOpened: boolean;
};

type StatisticSignStyledT = {
  isPositive: boolean;
};

type InvoicePercentageStyledT = {
  isPositive: boolean;
};

const reportHeaderHeight = 48;
const cardsBlockHeight = 185;
const cardsClosedTranslateValue = -(reportHeaderHeight + cardsBlockHeight);

export const DealerReportWrapStyled = styled.div`
  position: relative;
  width: 100%;
`;

export const CardsWrapStyled = styled(FlexContainer)<ReportStateStyledT>`
  flex-wrap: nowrap;
  gap: 4px;
  width: 100%;
  max-height: ${({ isReportOpened }): string => (isReportOpened ? 'none' : '0')};
  margin-top: 0;
  overflow: ${({ isReportOpened }): string => (isReportOpened ? 'visible' : 'hidden')};
  transform: ${({ isReportOpened }): string =>
    isReportOpened ? 'translateY(0)' : `translateY(${cardsClosedTranslateValue}px)`};
  z-index: 0;
  transition: max-height 0.25s linear, transform 0.3s linear;
`;

export const StatisticSignStyled = styled.div<StatisticSignStyledT>`
  width: 0;
  height: 0;
  margin: 0 4px 0 6px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: ${({ isPositive, theme }): string =>
    isPositive ? `6px solid ${theme.colors.secondary.variant2}` : 'none'};
  border-top: ${({ isPositive, theme }): string =>
    isPositive ? 'none' : `6px solid ${theme.colors.highlight.variant5}`};
`;

export const InvoicePercentageStyled = styled(Typography)<InvoicePercentageStyledT>`
  color: ${({ isPositive, theme }): string =>
    isPositive ? theme.colors.secondary.variant1 : theme.colors.highlight.variant2};
`;

export const StatisticSignLargeStyled = styled(StatisticSignStyled)`
  margin: 0 2px;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: ${({ isPositive, theme }): string =>
    isPositive ? `8px solid ${theme.colors.secondary.variant2}` : 'none'};
  border-top: ${({ isPositive, theme }): string =>
    isPositive ? 'none' : `8px solid ${theme.colors.highlight.variant5}`};
`;

type ChangePercentTextStyledT = {
  $variant: 'zero' | 'increase' | 'decrease';
  $negativeIsGood?: boolean;
};
const getChangePercentColor = (
  theme: DefaultTheme,
  variant: ChangePercentTextStyledT['$variant'],
  negativeIsGood?: boolean
): string => {
  if (variant === 'zero') return negativeIsGood ? theme.colors.white : theme.colors.black;
  const green = theme.colors.secondary.variant2;
  const red = theme.colors.highlight.variant2;
  if (negativeIsGood) {
    return variant === 'decrease' ? green : red;
  }
  return variant === 'increase' ? green : red;
};
export const ChangePercentTextStyled = styled.span<ChangePercentTextStyledT>`
  font-size: ${({ theme }): string => theme.fontSize.sm};
  font-weight: ${({ theme }): string => theme.fontWeight.medium.toString()};
  color: ${({ theme, $variant, $negativeIsGood }): string =>
    getChangePercentColor(theme, $variant, $negativeIsGood)};
`;

type ChangeTriangleStyledT = { $isIncrease: boolean; $negativeIsGood?: boolean };
const getTriangleStyles = (
  isIncrease: boolean,
  negativeIsGood: boolean | undefined,
  theme: DefaultTheme
): { bottom: string; top: string } => {
  const green = theme.colors.secondary.variant2;
  const red = theme.colors.highlight.variant2;
  const isGood = negativeIsGood ? !isIncrease : isIncrease;
  const color = isGood ? green : red;
  return {
    bottom: isIncrease ? `6px solid ${color}` : 'none',
    top: isIncrease ? 'none' : `6px solid ${color}`,
  };
};
export const ChangeTriangleStyled = styled.span<ChangeTriangleStyledT>`
  display: inline-block;
  width: 0;
  height: 0;
  margin: 0 4px 0 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: ${({ $isIncrease, $negativeIsGood, theme }): string =>
    getTriangleStyles($isIncrease, $negativeIsGood, theme).bottom};
  border-top: ${({ $isIncrease, $negativeIsGood, theme }): string =>
    getTriangleStyles($isIncrease, $negativeIsGood, theme).top};
`;
